import { useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import { GovMessage } from '@gov-design-system-ce/react';
import { UiBlockingGroupContext } from '@gov-nx/component/common';
import { MarkdownRender } from '@gov-nx/component/web';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DataBoxNotificationToggleInputName,
	PageCode,
	useDatovkaNastaveniUpozorneniContext,
} from '@gov-nx/module/page';
import { DataTable, FormSwitch, Icon, InfoTooltip } from '@gov-nx/ui/web';
import { DataboxConfirmExtendedMailNotificationModal } from './DataboxConfirmExtendedMailNotificationModal';
import { DataboxNotificationSettingsSkeleton } from './DataboxNotificationSettingsSkeleton';

export const DataboxNotificationSettings = () => {
	const context = useDatovkaNastaveniUpozorneniContext();
	const uiBlocking = useContext(UiBlockingGroupContext);
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['nastaveni']);
	const tds = getLocalizeCurried(PageCode['datove-schranky']);

	if (!context) {
		return null;
	}

	const {
		controls,
		storeNotificationSettings,
		handleExtendedMailNotificationChange,
		formInstance,
		databox,
		noContactDetails,
		disableEmailOptions,
		disablePhoneOptions,
		isEmailAttachmentChecked,
	} = context;

	if (controls.initialLoading || !databox) {
		return <DataboxNotificationSettingsSkeleton />;
	}

	const databoxId = databox.datovaSchrankaId;

	return (
		<FormProvider {...formInstance}>
			{noContactDetails && (
				<GovMessage
					color="warning"
					type="subtle"
					icon={
						<Icon
							type="components"
							name="info-circle-fill"
						/>
					}>
					{tn('upozorneni.datovka.zadne-kontaktni-udaje')}
				</GovMessage>
			)}
			<div className="gov-table">
				<DataTable>
					<thead className="text-body-s [ md:text-body-m ]">
						<tr>
							<th className="w-auto">
								<span className="invisible">{tn('table.header.druh')}</span>
							</th>
							<th className="font-normal w-[65px] py-2">
								{tn('table.header.email')}
							</th>
							<th className="font-normal w-[65px] py-2">
								{tn('table.header.sms')}
							</th>
						</tr>
					</thead>

					<tbody>
						<tr>
							<td className="text-body-s-bold">
								<label
									id={`popisek-nastaveni-upozorneni-notifikace_${databoxId}`}>
									{tds('nastaveni-upozorneni.notifikace')}
								</label>
								<InfoTooltip
									placement="right"
									content={
										<MarkdownRender>
											{tds('nastaveni-upozorneni.notifikace-info')}
										</MarkdownRender>
									}
								/>
							</td>
							<td className="cell-email">
								<FormSwitch
									field={{
										onChange: storeNotificationSettings,
										name: `${databoxId}.${DataBoxNotificationToggleInputName.Email}`,
										isDisabled:
											controls.processLoading ||
											uiBlocking?.isBlockedEditation ||
											disableEmailOptions,
										hasNoLabel: true,
										'aria-describedby': `popis-nastaveni-upozorneni-notifikace_${databoxId}`,
									}}
								/>
							</td>
							<td className="cell-sms">
								<FormSwitch
									field={{
										onChange: storeNotificationSettings,
										name: `${databoxId}.${DataBoxNotificationToggleInputName.Sms}`,
										isDisabled:
											controls.processLoading ||
											uiBlocking?.isBlockedEditation ||
											disablePhoneOptions,
										hasNoLabel: true,
										'aria-describedby':
											'popisek-nastaveni-upozorneni-notifikace',
									}}
								/>
							</td>
						</tr>
						<tr>
							<td className="text-body-s-bold">
								<label
									id={`popisek-nastaveni-upozorneni-preposlat-prilohy_${databoxId}`}>
									{tds('nastaveni-upozorneni.preposlat-prilohy')}
								</label>
								<InfoTooltip
									placement="right"
									content={
										<MarkdownRender>
											{tds('nastaveni-upozorneni.preposlat-prilohy-info')}
										</MarkdownRender>
									}
								/>
							</td>
							<td className="cell-email-attachment">
								<FormSwitch
									field={{
										onChange: handleExtendedMailNotificationChange,
										name: `${databoxId}.${DataBoxNotificationToggleInputName.EmailAttachment}`,
										isDisabled:
											controls.processLoading ||
											uiBlocking?.isBlockedEditation ||
											disableEmailOptions,
										hasNoLabel: true,
										'aria-describedby': `popisek-nastaveni-upozorneni-preposlat-prilohy_${databoxId}`,
									}}
								/>
								<DataboxConfirmExtendedMailNotificationModal />
							</td>
							<td className="hidden md:table-cell">&nbsp;</td>
						</tr>
					</tbody>
				</DataTable>
				{isEmailAttachmentChecked ? (
					<GovMessage
						color="warning"
						type="subtle"
						className="mt-(--templates-margin-m)!"
						icon={
							<Icon
								type="components"
								name="info-circle-fill"
							/>
						}>
						{tn('upozorneni.nastaveni.velkokapacitni-zpravy')}
					</GovMessage>
				) : null}
			</div>
		</FormProvider>
	);
};
